import { createSlice, type Draft, PayloadAction } from "@reduxjs/toolkit";

import type { Region } from "@packages/client";

import {
  createInitialOrganizationState,
  errorTypes
} from "./projectCreation.types";

import type {
  ErrorType,
  GithubBranch,
  GithubOrganization,
  GithubRepository,
  GitProvider,
  ProjectCreationSliceState,
  OnboardingType,
  OrganizationProjectCreationState,
  VerificationType
} from "./projectCreation.types";

// ============================================================================
// Initial State
// ============================================================================

export const initialState: ProjectCreationSliceState = {
  byOrganizationId: {}
};

// ============================================================================
// Helper to ensure organization state exists
// ============================================================================

const ensureOrganizationState = (
  state: Draft<ProjectCreationSliceState>,
  organizationId: string
): Draft<OrganizationProjectCreationState> => {
  if (!state.byOrganizationId[organizationId]) {
    state.byOrganizationId[organizationId] =
      createInitialOrganizationState() as Draft<OrganizationProjectCreationState>;
  }
  return state.byOrganizationId[organizationId];
};

// ============================================================================
// Slice Definition
// ============================================================================

const projectCreationSlice = createSlice({
  name: "projectCreation",
  initialState,
  reducers: {
    // ========================================================================
    // Wizard Actions
    // ========================================================================

    setOnboardingType: (
      state,
      action: PayloadAction<{
        organizationId: string;
        onboardingType: OnboardingType;
      }>
    ) => {
      const { organizationId, onboardingType } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.wizard.onboardingType = onboardingType;
    },

    // ========================================================================
    // Project Details Actions
    // ========================================================================

    setProjectName: (
      state,
      action: PayloadAction<{
        organizationId: string;
        projectName: string;
      }>
    ) => {
      const { organizationId, projectName } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectDetails.projectName = projectName;
    },

    setProductionEnvironment: (
      state,
      action: PayloadAction<{
        organizationId: string;
        productionEnvironment: string;
      }>
    ) => {
      const { organizationId, productionEnvironment } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectDetails.productionEnvironment = productionEnvironment;
    },

    setRegion: (
      state,
      action: PayloadAction<{
        organizationId: string;
        region: Region;
      }>
    ) => {
      const { organizationId, region } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectDetails.region = region;
    },

    // ========================================================================
    // Git Provider Actions
    // ========================================================================

    setGitProvider: (
      state,
      action: PayloadAction<{
        organizationId: string;
        provider: GitProvider;
      }>
    ) => {
      const { organizationId, provider } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.provider = provider;
    },

    setGitProviderLoading: (
      state,
      action: PayloadAction<{
        organizationId: string;
        isLoading: boolean;
      }>
    ) => {
      const { organizationId, isLoading } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.isLoading = isLoading;
    },

    setGitProviderError: (
      state,
      action: PayloadAction<{
        organizationId: string;
        error: string | null;
      }>
    ) => {
      const { organizationId, error } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.error = error;
      orgState.gitProvider.isLoading = false;
    },

    // GitHub-specific actions
    setGithubOrganizations: (
      state,
      action: PayloadAction<{
        organizationId: string;
        organizations: GithubOrganization[];
      }>
    ) => {
      const { organizationId, organizations } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.github.organizations = organizations;
      orgState.gitProvider.github.hasFetchedOrganizations = true;
    },

    setSelectedGithubOrganization: (
      state,
      action: PayloadAction<{
        organizationId: string;
        installationId: string | null;
        label: string | null;
      }>
    ) => {
      const { organizationId, installationId, label } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.github.selectedOrganizationId = installationId;
      orgState.gitProvider.github.selectedOrganizationLabel = label;
      orgState.gitProvider.github.installationId = installationId;
      // Clear downstream selections when organization changes
      orgState.gitProvider.github.repositories = [];
      orgState.gitProvider.github.selectedRepository = null;
      orgState.gitProvider.github.selectedRepositoryDefaultBranch = null;
      orgState.gitProvider.github.branches = [];
      orgState.gitProvider.github.selectedBranch = null;
    },

    setGithubRepositories: (
      state,
      action: PayloadAction<{
        organizationId: string;
        repositories: GithubRepository[];
      }>
    ) => {
      const { organizationId, repositories } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.github.repositories = repositories;
    },

    setSelectedGithubRepository: (
      state,
      action: PayloadAction<{
        organizationId: string;
        repository: string | null;
        defaultBranch: string | null;
      }>
    ) => {
      const { organizationId, repository, defaultBranch } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.github.selectedRepository = repository;
      orgState.gitProvider.github.selectedRepositoryDefaultBranch =
        defaultBranch;
      // Clear branch selection when repository changes
      orgState.gitProvider.github.branches = [];
      orgState.gitProvider.github.selectedBranch = null;
    },

    setGithubBranches: (
      state,
      action: PayloadAction<{
        organizationId: string;
        branches: GithubBranch[];
      }>
    ) => {
      const { organizationId, branches } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.github.branches = branches;
    },

    setSelectedGithubBranch: (
      state,
      action: PayloadAction<{
        organizationId: string;
        branch: string | null;
      }>
    ) => {
      const { organizationId, branch } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.gitProvider.github.selectedBranch = branch;
    },

    // ========================================================================
    // Verification Actions
    // ========================================================================

    setVerificationType: (
      state,
      action: PayloadAction<{
        organizationId: string;
        verificationType: VerificationType;
      }>
    ) => {
      const { organizationId, verificationType } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.verification.type = verificationType;
    },

    setVerificationCredentials: (
      state,
      action: PayloadAction<{
        organizationId: string;
        credentials: {
          client_secret?: string;
          public_key?: string;
          automatic_payment_methods?: boolean;
        } | null;
      }>
    ) => {
      const { organizationId, credentials } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.verification.credentials = credentials;
    },

    // ========================================================================
    // Project Creation Actions
    // ========================================================================

    setProjectCreationLoading: (
      state,
      action: PayloadAction<{
        organizationId: string;
        isLoading: boolean;
      }>
    ) => {
      const { organizationId, isLoading } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectCreation.isLoading = isLoading;
    },

    setProjectCreationError: (
      state,
      action: PayloadAction<{
        organizationId: string;
        errorType: ErrorType;
      }>
    ) => {
      const { organizationId, errorType } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectCreation.errorType = errorType;
      orgState.projectCreation.isLoading = false;
    },

    /**
     * Marks the flow as finished, which is what makes its history entries
     * spent — see `hasCreatedProject`. Only `resetOnboardingState` clears it,
     * so opening the flow again is what starts a new one.
     */
    setProjectCreated: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      const { organizationId } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectCreation.hasCreatedProject = true;
    },

    clearProjectCreationError: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      const { organizationId } = action.payload;
      const orgState = ensureOrganizationState(state, organizationId);
      orgState.projectCreation.errorType = errorTypes.default;
    },

    // ========================================================================
    // Reset Actions
    // ========================================================================

    /**
     * Clears the flow's input so the next project starts from nothing.
     *
     * The list of GitHub organizations survives it. That list is the
     * organization's answer to "is the App installed", not something the user
     * typed: it decides which page the flow opens on, and no choice made inside
     * the flow can change it. Wiping it on the way back to the list put the
     * page behind a spinner and refetched what it already had.
     */
    resetOnboardingState: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      const { organizationId } = action.payload;
      const github = state.byOrganizationId[organizationId]?.gitProvider.github;
      const next =
        createInitialOrganizationState() as Draft<OrganizationProjectCreationState>;

      next.gitProvider.github.organizations = github?.organizations ?? [];
      next.gitProvider.github.hasFetchedOrganizations =
        github?.hasFetchedOrganizations ?? false;

      state.byOrganizationId[organizationId] = next;
    }
  }
});

// ============================================================================
// Exports
// ============================================================================

export const projectCreationActions = projectCreationSlice.actions;
export const projectCreationReducer = projectCreationSlice.reducer;

// Re-export types and constants for convenience
export {
  onboardingTypes,
  errorTypes,
  verificationTypes,
  onboardingPhases,
  phaseToPath,
  showProvidersParam,
  fromParam,
  connectParam,
  gitProviders,
  isGitProvider,
  isOnboardingType
} from "./projectCreation.types";
